import { useEffect } from "react"
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom"
import { QueryClientProvider } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { queryClient } from "@/lib/queryClient"
import { errorReporter } from "@/lib/errorReporter"
import { Toaster } from "@/components/ui/sonner"
import { AppErrorBoundary } from "@/components/common/AppErrorBoundary"
import { Generator } from "@/pages/Generator"
import { Forbidden } from "@/pages/Forbidden"
import { WrksSSOCallback } from "@/pages/WrksSSOCallback"
import { OAuthCallback } from '@/pages/OAuthCallback'
import { SharePage } from "@/pages/SharePage"
import { AdminRoute } from "@/components/common/AdminRoute"
import { AdminLayout } from "@/features/admin/layout/AdminLayout"
import { Dashboard } from "@/features/admin/dashboard/Dashboard"
import { TemplateList } from "@/features/admin/templates/TemplateList"
import { TemplateDetail } from "@/features/admin/templates/TemplateDetail"
import { TemplateForm } from "@/features/admin/templates/TemplateForm"
import { TemplateCandidateList, TemplateCandidateDetail } from "@/features/admin/templates/TemplateCandidateReview"
import { StyleList } from "@/features/admin/styles/StyleList"
import { StyleDetail } from "@/features/admin/styles/StyleDetail"
import { StyleForm } from "@/features/admin/styles/StyleForm"
import { CategoryList } from "@/features/admin/categories/CategoryList"
import { CategoryDetail } from "@/features/admin/categories/CategoryDetail"
import { CategoryForm } from "@/features/admin/categories/CategoryForm"
import { GenerationList } from "@/features/admin/generations/GenerationList"
import { GenerationDetail } from "@/features/admin/generations/GenerationDetail"
import { GenerationForm } from "@/features/admin/generations/GenerationForm"
import { FailedImagesList } from "@/features/admin/failed-images/FailedImagesList"
import { FailedImageDetail } from "@/features/admin/failed-images/FailedImageDetail"
import { SharedImagesList } from "@/features/admin/shared-images/SharedImagesList"
import { SharedImageDetail } from "@/features/admin/shared-images/SharedImageDetail"
import { UserList } from "@/features/admin/users/UserList"
import { UserDetail } from "@/features/admin/users/UserDetail"
import { SettingsPage } from "@/features/admin/settings/SettingsPage"
import { ErrorList } from "@/features/admin/errors/ErrorList"
import { ErrorDetail } from "@/features/admin/errors/ErrorDetail"

// 조건부 basename: 로컬은 '', 프로덕션은 '/image'
const basename = import.meta.env.VITE_BASE_PATH?.replace(/\/$/, '') || ''

/**
 * 언어 변경 시 document.title / meta description 갱신.
 * 어드민(/admin)은 한국어 고정이므로 건드리지 않는다.
 */
function DocumentLocalization() {
  const { t, i18n } = useTranslation()
  const { pathname } = useLocation()

  useEffect(() => {
    if (pathname.startsWith('/admin')) return
    document.title = t('meta.title')
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute('content', t('meta.description'))
  }, [t, i18n.language, pathname])

  return null
}

/** 라우트 이동을 에러 리포트 브레드크럼으로 기록 */
function RouteBreadcrumbs() {
  const { pathname } = useLocation()

  useEffect(() => {
    errorReporter.addBreadcrumb({ type: 'route', message: pathname })
  }, [pathname])

  return null
}

function App() {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={basename}>
          <DocumentLocalization />
          <RouteBreadcrumbs />
          <Routes>
            <Route path="/" element={<Generator />} />
            <Route path="/my" element={<Generator />} />
            <Route path="/templates" element={<Generator />} />
            <Route path="/templates/:categoryId" element={<Generator />} />
            <Route path="/community" element={<Generator />} />
            <Route path="/share/:shareId" element={<SharePage />} />
            <Route path="/auth" element={<WrksSSOCallback />} />
            <Route path="/auth/callback" element={<OAuthCallback />} />
            <Route path="/forbidden" element={<Forbidden />} />

            {/* Admin Routes - Protected */}
            <Route element={<AdminRoute />}>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="templates" element={<TemplateList />} />
                <Route path="templates/new" element={<TemplateForm />} />
                <Route path="templates/candidates" element={<TemplateCandidateList />} />
                <Route path="templates/candidates/:id/view" element={<TemplateCandidateDetail />} />
                <Route path="templates/:id/view" element={<TemplateDetail />} />
                <Route path="templates/:id/edit" element={<TemplateForm />} />
                <Route path="styles" element={<StyleList />} />
                <Route path="styles/new" element={<StyleForm />} />
                <Route path="styles/:id/view" element={<StyleDetail />} />
                <Route path="styles/:id/edit" element={<StyleForm />} />
                <Route path="categories" element={<CategoryList />} />
                <Route path="categories/new" element={<CategoryForm />} />
                <Route path="categories/:id/view" element={<CategoryDetail />} />
                <Route path="categories/:id/edit" element={<CategoryForm />} />
                <Route path="generations" element={<GenerationList />} />
                <Route path="generations/:id/view" element={<GenerationDetail />} />
                <Route path="generations/:id/edit" element={<GenerationForm />} />
                <Route path="failed-images" element={<FailedImagesList />} />
                <Route path="failed-images/:id/view" element={<FailedImageDetail />} />
                <Route path="shared-images" element={<SharedImagesList />} />
                <Route path="shared-images/:shareId/view" element={<SharedImageDetail />} />
                <Route path="users" element={<UserList />} />
                <Route path="users/:id/view" element={<UserDetail />} />
                <Route path="errors" element={<ErrorList />} />
                <Route path="errors/:id" element={<ErrorDetail />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>
            </Route>
          </Routes>
          <Toaster
            position="top-right"
            richColors
            closeButton
            expand
            visibleToasts={5}
            duration={5000}
          />
        </BrowserRouter>
      </QueryClientProvider>
    </AppErrorBoundary>
  )
}

export default App
